import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getNotifications, markAllNotificationsRead } from '../services/notifications'
import { useEmulation } from '../contexts/EmulationContext'

const NotificationsDropdown = () => {
  const { t, i18n } = useTranslation('forms')
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [processingEmulation, setProcessingEmulation] = useState(null) // sessionId being processed
  const [emulationError, setEmulationError] = useState(null)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const { approveEmulation, denyEmulation, startEmulation } = useEmulation()

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const data = await getNotifications({ limit: 20 })
      setNotifications(data.notifications || [])
      setUnreadCount(data.unreadCount || 0)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications()

    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Mark all as read when dropdown opens
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      // Optimistically update UI immediately
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)

      // Fire API call in background
      markAllNotificationsRead().catch(error => {
        console.error('Failed to mark all as read:', error)
      })
    }
  }, [isOpen])

  // Handle notification click - just navigate, since all are marked read on open
  const handleNotificationClick = async (notification) => {
    // PDF export complete — trigger download
    if (notification.type === 'pdf_export_complete' && notification.metadata?.downloadUrl) {
      window.open(notification.metadata.downloadUrl, '_blank')
      return
    }

    // Cold storage archive restore complete (#19) — trigger download
    if (notification.type === 'archive_restore_complete' && notification.metadata?.downloadUrl) {
      window.open(notification.metadata.downloadUrl, '_blank')
      return
    }

    // Handle emulation_approved notification - start the session
    if (notification.type === 'emulation_approved') {
      const sessionId = notification.metadata?.sessionId
      if (sessionId) {
        setProcessingEmulation(sessionId)
        try {
          const result = await startEmulation(sessionId)
          if (result.success) {
            setIsOpen(false)
            // Remove the notification from the list
            setNotifications(prev => prev.filter(n => n.notificationId !== notification.notificationId))
            // Refresh page to show emulation banner
            window.location.reload()
          } else {
            setEmulationError(result.error || 'Failed to start emulation session')
          }
        } catch (err) {
          setEmulationError(err.message || 'Failed to start emulation session')
        } finally {
          setProcessingEmulation(null)
        }
      }
      return
    }

    setIsOpen(false)

    // Navigate to the collaborative submission page
    if (notification.submissionId && notification.formId) {
      navigate(`/submissions/${notification.formId}/${notification.submissionId}`)
    }
  }

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return t('notifications.time.justNow')
    if (diffMins < 60) return t('notifications.time.minutesAgo', { count: diffMins })
    if (diffHours < 24) return t('notifications.time.hoursAgo', { count: diffHours })
    if (diffDays < 7) return t('notifications.time.daysAgo', { count: diffDays })
    return date.toLocaleDateString(i18n.language)
  }

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'section_available':
        return '🔓'
      case 'section_completed':
        return '✅'
      case 'submission_complete':
        return '🎉'
      case 'assignment_changed':
        return '📋'
      // Review workflow notifications
      case 'review_requested':
        return '👀'
      case 'stage_approved':
        return '✓'
      case 'workflow_approved':
        return '🏆'
      case 'submission_returned':
        return '↩️'
      case 'parallel_progress':
        return '📊'
      // Deadline notifications (#98)
      case 'task_deadline_approaching':
        return '⏰'
      case 'task_deadline_overdue':
        return '⚠️'
      // PDF export notifications
      case 'pdf_export_complete':
        return '📄'
      case 'pdf_export_failed':
        return '❌'
      // Cold storage archive restore notifications (#19)
      case 'archive_restore_complete':
        return '🗄️'
      // Emulation notifications
      case 'emulation_request':
        return '👁️'
      case 'emulation_approved':
        return '✅'
      case 'emulation_denied':
        return '❌'
      default:
        return '🔔'
    }
  }

  // Handle emulation approve/deny actions
  const handleEmulationAction = async (notification, action) => {
    // The sessionId is stored in metadata for emulation notifications
    const sessionId = notification.metadata?.sessionId
    if (!sessionId) {
      console.error('No sessionId in emulation notification metadata:', notification)
      setEmulationError('Unable to process request - missing session ID')
      return
    }

    setProcessingEmulation(sessionId)
    try {
      let result
      if (action === 'approve') {
        result = await approveEmulation(sessionId)
      } else {
        result = await denyEmulation(sessionId)
      }

      if (result.success) {
        // Remove the notification from the list
        setNotifications(prev => prev.filter(n => n.notificationId !== notification.notificationId))
        setEmulationError(null)
      } else {
        setEmulationError(result.error || t('common:errors.operationFailed', 'Operation failed'))
      }
    } catch (err) {
      setEmulationError(err.message || t('common:errors.operationFailed', 'Operation failed'))
    } finally {
      setProcessingEmulation(null)
    }
  }

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <button
        className="notifications-button"
        onClick={() => setIsOpen(!isOpen)}
        title={t('notifications.unreadCount', { count: unreadCount })}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-panel">
          <div className="notifications-header">
            <h3>{t('notifications.title')}</h3>
          </div>

          {emulationError && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              fontSize: '0.875rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {emulationError}
              <button
                onClick={() => setEmulationError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 4px',
                  color: '#dc2626'
                }}
              >✕</button>
            </div>
          )}

          <div className="notifications-list">
            {loading && notifications.length === 0 ? (
              <div className="notification-empty">{t('notifications.loading')}</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">{t('notifications.empty')}</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.notificationId}
                  className={`notification-item ${!notification.read ? 'unread' : ''} ${notification.type === 'emulation_request' ? 'notification-item--no-click' : ''}`}
                  onClick={() => notification.type !== 'emulation_request' && handleNotificationClick(notification)}
                >
                  <span className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>
                    {/* Emulation request notifications have approve/deny buttons */}
                    {notification.type === 'emulation_request' && (
                      <div className="notification-actions">
                        <button
                          className="notification-action-btn notification-action-btn--approve"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEmulationAction(notification, 'approve')
                          }}
                          disabled={processingEmulation === notification.metadata?.sessionId}
                        >
                          {processingEmulation === notification.metadata?.sessionId ? '...' : t('common:buttons.approve', 'Approve')}
                        </button>
                        <button
                          className="notification-action-btn notification-action-btn--deny"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEmulationAction(notification, 'deny')
                          }}
                          disabled={processingEmulation === notification.metadata?.sessionId}
                        >
                          {t('common:buttons.deny', 'Deny')}
                        </button>
                      </div>
                    )}
                    <div className="notification-time">
                      {formatTimeAgo(notification.createdAt)}
                    </div>
                  </div>
                  {!notification.read && (
                    <span className="unread-dot" />
                  )}
                </div>
              ))
            )}
          </div>

          <div className="notifications-footer">
            <button
              className="view-all-btn"
              onClick={() => {
                navigate('/my-tasks')
                setIsOpen(false)
              }}
            >
              {t('notifications.viewMyTasks')}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .notifications-dropdown {
          position: relative;
        }

        .notifications-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          position: relative;
          font-size: 1.25rem;
        }

        .notification-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          background: var(--color-danger);
          color: white;
          font-size: 0.7rem;
          font-weight: bold;
          padding: 2px 5px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
        }

        .notifications-panel {
          position: absolute;
          top: 100%;
          right: 0;
          width: 360px;
          max-height: 480px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          display: flex;
          flex-direction: column;
        }

        .notifications-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid #eee;
        }

        .notifications-header h3 {
          margin: 0;
          font-size: 1rem;
        }

        .notifications-list {
          flex: 1;
          overflow-y: auto;
          max-height: 360px;
        }

        .notification-item {
          display: flex;
          align-items: flex-start;
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.15s;
        }

        .notification-item:hover {
          background: #f8f9fa;
        }

        .notification-item.unread {
          background: #f0f7ff;
        }

        .notification-icon {
          font-size: 1.25rem;
          margin-right: 12px;
          flex-shrink: 0;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 2px;
        }

        .notification-message {
          font-size: 0.85rem;
          color: #555;
          line-height: 1.4;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .notification-time {
          font-size: 0.75rem;
          color: #999;
          margin-top: 4px;
        }

        .unread-dot {
          width: 8px;
          height: 8px;
          background: var(--color-primary);
          border-radius: 50%;
          flex-shrink: 0;
          margin-left: 8px;
          margin-top: 4px;
        }

        .notification-empty {
          padding: 32px 16px;
          text-align: center;
          color: #999;
        }

        .notifications-footer {
          padding: 12px 16px;
          border-top: 1px solid #eee;
        }

        .view-all-btn {
          width: 100%;
          padding: 8px;
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .view-all-btn:hover {
          background: var(--color-border);
        }

        .notification-item--no-click {
          cursor: default;
        }

        .notification-item--no-click:hover {
          background: inherit;
        }

        .notification-item--no-click.unread:hover {
          background: #f0f7ff;
        }

        .notification-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }

        .notification-action-btn {
          padding: 4px 12px;
          font-size: 0.75rem;
          font-weight: 500;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .notification-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .notification-action-btn--approve {
          background: var(--color-success);
          color: white;
        }

        .notification-action-btn--approve:hover:not(:disabled) {
          background: var(--color-success-hover);
        }

        .notification-action-btn--deny {
          background: var(--color-danger);
          color: white;
        }

        .notification-action-btn--deny:hover:not(:disabled) {
          background: var(--color-danger-hover);
        }
      `}</style>
    </div>
  )
}

export default NotificationsDropdown
